import React, { useEffect, useRef, useState } from 'react';
import { buildReactPreviewHTML, buildAngularPreviewHTML } from './buildReactPreviewHTML';

const UIPreview = ({
  language = 'react',
  files = {},
  isDarkMode = false,
  isLoading = false,
}) => {
  const iframeRef = useRef(null);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(0);

  // Generate preview HTML when files change
  useEffect(() => {
    if (!iframeRef.current) return;

    // Debounce to prevent rapid updates
    const timer = setTimeout(() => {
      try {
        setError(null);

        let htmlContent;
        if (language === 'react') {
          htmlContent = buildReactPreviewHTML(files);
        } else if (language === 'angular') {
          htmlContent = buildAngularPreviewHTML(files);
        } else {
          setError('Unsupported language for preview');
          return;
        }

        // Use srcdoc to set iframe content (no XSS risk due to sandbox)
        iframeRef.current.srcdoc = htmlContent;
      } catch (err) {
        setError(`Error building preview: ${err.message}`);
        console.error('Preview build error:', err);
      }
    }, 300); // Debounce delay

    return () => clearTimeout(timer);
  }, [files, language]);

  return (
    <div
      className={`flex flex-col h-full rounded-lg overflow-hidden ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}
    >
      {/* Preview Header */}
      <div
        className={`flex items-center justify-between px-3 py-2 border-b ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600'
            : 'bg-[#F6F3FC] border-[#E6E0F3]'
        }`}
      >
        <h4 className="text-sm font-semibold flex items-center space-x-2">
          <span>🎨</span>
          <span>{language === 'react' ? 'React Preview' : 'Angular Preview'}</span>
        </h4>
        {isLoading && (
          <div className="text-xs text-[#6A1FD0] animate-pulse">Updating...</div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-700 dark:text-red-300 text-sm">
          <strong>⚠️ Error:</strong> {error}
        </div>
      )}

      {/* Preview iframe */}
      <iframe
        ref={iframeRef}
        className="flex-1 w-full border-0"
        title={`${language} Preview`}
        sandbox="allow-scripts"
        style={{
          backgroundColor: isDarkMode ? '#1e1e1e' : '#fff',
        }}
      />
    </div>
  );
};

export default UIPreview;
